import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './MC.css';
import api from './api';
import LanguageSwitcher from './components/LanguageSwitcher';
import ThemeToggle from './components/ThemeToggle';

function MC() {
  const { t } = useTranslation();
  const [playlist, setPlaylist] = useState([]);

  // Register service worker and subscribe to Web Push
  useEffect(() => {
    const setup = async () => {
      if (!('serviceWorker' in navigator) || !('Notification' in window)) return;

      // Request permission
      if (Notification.permission === 'default') {
        await Notification.requestPermission();
      }

      try {
        const registration = await navigator.serviceWorker.register('/zapisy/sw-notifications.js');

        if (Notification.permission === 'granted') {
          const vapidResponse = await api.get('/api/push/vapid-public-key');
          const vapidPublicKey = vapidResponse.data.publicKey;

          const urlBase64ToUint8Array = (base64String) => {
            const padding = '='.repeat((4 - base64String.length % 4) % 4);
            const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
            const rawData = window.atob(base64);
            const outputArray = new Uint8Array(rawData.length);
            for (let i = 0; i < rawData.length; ++i) {
              outputArray[i] = rawData.charCodeAt(i);
            }
            return outputArray;
          };

          const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
          });

          await api.post('/api/push/subscribe', subscription.toJSON());
        }
      } catch (err) {
        console.error('Push setup failed:', err);
      }
    };
    setup();
  }, []);

  const loadData = useCallback(async () => {
    try {
      const response = await api.get('/api/playlist');
      setPlaylist(response.data || []);
    } catch (err) {
      console.error('Failed to load playlist:', err);
    }
  }, []);

  useEffect(() => {
    loadData();
    const dataInterval = setInterval(loadData, 3000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') loadData();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      clearInterval(dataInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadData]);

  const formatDuration = (duration) => {
    if (!duration) return '';
    if (duration.includes(':')) return duration;
    const secs = parseInt(duration, 10);
    if (isNaN(secs)) return duration;
    const mins = Math.floor(secs / 60);
    const remainingSecs = secs % 60;
    return `${mins.toString().padStart(2, '0')}:${remainingSecs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="mc-app">
      <header className="mc-header">
        <ThemeToggle />
        <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Karaoke Imprezy" className="mc-header-logo" />
        <LanguageSwitcher />
      </header>

      <div className="mc-content">
        <div className="mc-playlist">
          <h2>{t('mc.playlist')} ({playlist.length})</h2>

          {playlist.length === 0 ? (
            <div className="mc-empty">{t('mc.emptyPlaylist')}</div>
          ) : (
            <div className="mc-list">
              {playlist.map((item, index) => {
                const isPlaceholder = item.is_placeholder;
                const placeholderClass = isPlaceholder ? `mc-row-placeholder ${item.placeholder_type}` : '';

                return (
                  <div key={item.id} className={`mc-row ${placeholderClass}`}>
                    <div className="mc-position">{index + 1}</div>
                    <div className="mc-info">
                      {isPlaceholder ? (
                        <div className="mc-song mc-placeholder-title">
                          <span className="mc-placeholder-emoji">
                            {item.placeholder_type === 'dance_break' ? '💃🕺' : '🎤🎶'}
                          </span>
                          <strong>
                            {item.placeholder_type === 'dance_break'
                              ? t('dj.danceBreakTitle')
                              : t('dj.resumeKaraokeTitle')}
                          </strong>
                        </div>
                      ) : (
                        <>
                          <div className="mc-song">
                            <strong>{item.title}</strong> - {item.artist}
                          </div>
                          <div className="mc-meta">
                            <span className="mc-singer">{item.singer_name}</span>
                            {item.tonation !== 0 && (
                              <span className={`mc-tonation ${item.tonation > 0 ? 'positive' : 'negative'}`}>
                                {item.tonation > 0 ? `+${item.tonation}` : item.tonation}
                              </span>
                            )}
                            <span className="mc-duration">{formatDuration(item.duration)}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MC;
